import { type KeyboardEvent, type RefObject, useCallback } from 'react';

/**
 * useEscapeToBlur — returns a keydown handler that makes an
 * otherwise-unclaimed Escape press exit an editing context by blurring the
 * focused element, dropping keyboard focus back to the page so global
 * shortcuts (e.g. ⌘↑/⌘↓ action navigation) work again immediately.
 *
 * Layering — one press steps out one layer:
 * - Inner popups that consume Escape (mention menus, recipient dropdowns)
 *   call preventDefault; the defaultPrevented check leaves those presses to
 *   them.
 * - Pass `disabled` while another layer with its own Escape behavior (e.g.
 *   an edit mode closed by a document-level handler) should claim the press.
 * - Menus that portal OUTSIDE the container are untouched by the
 *   focus-containment check and keep their own document-level close.
 *
 * Attach the returned handler to the editing container's `onKeyDown`
 * (bubble phase — capture would run before inner consumers).
 */
export function useEscapeToBlur<T extends HTMLElement>(
  containerRef: RefObject<T | null>,
  disabled = false
) {
  return useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'Escape' || e.defaultPrevented || disabled) {
        return;
      }
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        containerRef.current?.contains(active)
      ) {
        e.preventDefault();
        active.blur();
      }
    },
    [containerRef, disabled]
  );
}
